import { useMutation, useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { Bill } from "@luckymenu/billing-core";
import { uuidv7 } from "@luckymenu/core";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	cashSuggestions,
	EMPTY_PAYMENT_DRAFT,
	type PaymentDraft,
	paymentAmount,
	paymentFromDraft,
	paymentUpiLink,
} from "../billing/payment";
import { type BillingError, billingProblemOf } from "../billing/problems";
import { paiseToRupees } from "../menu/form-errors";
import { useStaffCore } from "../provider";
import { useBillCache, useBillingSettings } from "./use-bill";
import { useCurrentMembership } from "./use-current";
import { useCan } from "./use-navigation";

/**
 * Taking payment for an order's bill (OP-12, P4-T17): pick a method, the
 * amount (all that is left, or less for a split), cash received for the
 * change, then `record()`. Each payment has a request ID made when it is
 * started and kept until it is recorded, so a retry never records twice.
 * The screen stays for the next part until nothing is left (`paid`).
 *
 * `upi` is the link for the full-screen QR (P4-T18) of this payment's
 * amount, to the restaurant's UPI ID now, or null without one.
 */
export function usePayment(
	orderId: string,
	options: { method?: PaymentDraft["method"]; amountPaise?: number } = {},
) {
	const trpc = useTRPC();
	const allowed = useCan("bill.create");
	const canRecord = useCan("payment.record");
	const query = useQuery({
		...trpc.staff.billing.getBill.queryOptions({ orderId }),
		enabled: allowed,
	});
	const { settings } = useBillingSettings();
	const restaurantName = useCurrentMembership().data?.restaurant.name ?? "";
	// A diner's "I have paid" opens this with UPI and the amount they paid (P4-T22).
	const [draft, setDraft] = useState<PaymentDraft>(() => ({
		...EMPTY_PAYMENT_DRAFT,
		method: options.method ?? EMPTY_PAYMENT_DRAFT.method,
		amount: options.amountPaise ? paiseToRupees(options.amountPaise) : "",
	}));
	const [problem, setProblem] = useState<BillingError | null>(null);
	const [lastChangePaise, setLastChangePaise] = useState<number | null>(null);
	const requestId = useRef(uuidv7());
	const cache = useBillCache();
	const print = useStaffCore().print;
	const mutation = useMutation(
		trpc.staff.billing.recordPayment.mutationOptions({ onSuccess: (bill) => cache(bill) }),
	);
	const bill = query.data ?? null;
	const balancePaise = bill?.balancePaise ?? 0;
	const check = paymentFromDraft(draft, balancePaise);
	const amountPaise = paymentAmount(draft, balancePaise);

	const upi = useMemo(() => {
		if (!bill || draft.method !== "upi" || amountPaise === null || amountPaise <= 0) return null;
		const payeeName = settings?.upiPayeeName || restaurantName;
		const forPaise = Math.min(amountPaise, balancePaise);
		const link = paymentUpiLink({
			vpa: settings?.upiVpa ?? null,
			payeeName,
			amountPaise: forPaise,
			billNo: bill.billNo,
			billId: bill.id,
		});
		return link ? { link, payeeName, amountPaise: forPaise } : null;
	}, [bill, draft.method, amountPaise, balancePaise, settings, restaurantName]);

	const record = useCallback(async (): Promise<Bill | null> => {
		if (!bill || !check.ok || mutation.isPending) return null;
		setProblem(null);
		try {
			const next = await mutation.mutateAsync({
				id: requestId.current,
				billId: bill.id,
				method: check.payment.method,
				amountPaise: check.payment.amountPaise,
				reference: check.payment.reference,
				note: check.payment.note,
			});
			requestId.current = uuidv7();
			// PR-10, P5-T28: the bill printer's cash drawer, when it has one this device reaches.
			if (check.payment.method === "cash") void print.openDrawer();
			setLastChangePaise(check.payment.method === "cash" ? check.changePaise : null);
			setDraft((d) => ({ ...EMPTY_PAYMENT_DRAFT, method: d.method }));
			return next;
		} catch (error) {
			setProblem(billingProblemOf(error));
			void query.refetch();
			return null;
		}
	}, [bill, check, mutation, query, print]);

	const loadProblem = query.isError ? billingProblemOf(query.error) : null;
	return {
		/** `notBilled`: the order has no bill (not billed yet, or its bill was voided). */
		status: bill
			? ("ready" as const)
			: loadProblem?.problem === "notFound"
				? ("notBilled" as const)
				: loadProblem
					? ("error" as const)
					: ("loading" as const),
		retry: () => void query.refetch(),
		bill,
		/** The role records payments (`payment.record`). */
		canRecord,
		/** Nothing left to pay on a final bill. */
		paid: bill !== null && bill.status === "final" && bill.balancePaise === 0,
		draft,
		set: useCallback(<K extends keyof PaymentDraft>(field: K, value: PaymentDraft[K]) => {
			setProblem(null);
			setLastChangePaise(null);
			setDraft((d) => ({ ...d, [field]: value }));
		}, []),
		/** The payment as typed, or what is wrong with it (`problems`, shown after a try). */
		check,
		/** This payment's amount: the typed one or all that is left; null while not an amount. */
		amountPaise,
		/** Notes a diner is likely to hand over for this amount, for one-tap entry. */
		suggestions: useMemo(
			() => (amountPaise && amountPaise > 0 ? cashSuggestions(amountPaise) : []),
			[amountPaise],
		),
		upi,
		/** The change for the last cash payment recorded, to say once more after it. */
		lastChangePaise,
		record,
		busy: mutation.isPending,
		problem,
		clearProblem: useCallback(() => setProblem(null), []),
	};
}
